import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthProvider";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useLastNameFirst } from "../../hooks/useNameFormat";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { getStaffingIssues, type StaffingIssue, type StaffingParams } from "../../utils/autoStaffing";
import { IssuePriority } from "../../utils/models";

export const useDayScheduleStaffingIssues = ({ dayIndex, weekIndex }: { dayIndex: number; weekIndex: number }) => {
	const { userInfo } = useAuth();
	const { hospitalData, isSnf, chosenDepartment, departmentNurseTypes, calculateShiftTimes } = useHospitalData();
	const { staffRequests } = useRequestsContext();

	const [recommendations, setRecommendations] = useState<StaffingIssue[]>([]);
	const [chosenLevel, setChosenLevel] = useState<IssuePriority>(IssuePriority.TopPriority);
	const [implementedChanges, setImplementedChanges] = useState<Record<string, boolean>>({});

	const startingDate = useMemo<DateTime>(() => hospitalData?.weeks[0]?.weeklyShifts[0]?.days[0]?.date ?? DateTime.now(), [hospitalData]);
	const isBlockRecommendationsV2Enabled = useFeatureFlag(FEATURE_FLAGS.BlockRecommendationsV2);
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const lastNameFirst = useLastNameFirst();
	const getRecommendations = useCallback((): void => {
		if (!isBlockRecommendationsV2Enabled) return;

		const staffingContext: StaffingParams = {
			hospitalData,
			weekIndex,
			isSnf,
			isAutoScheduler: false,
			chosenDepartment,
			timezone: chosenDepartment.timezone,
			departmentNurseTypes,
			requests: staffRequests,
			calculateShiftTimes,
			jobManagementEnabled,
			lastNameFirst,
		};

		const currentDayIssues = getStaffingIssues(staffingContext).filter(
			(issue) => issue.suggestions[0] && issue.suggestions[0].dayNo === dayIndex && issue.suggestions[0].weekIndex === weekIndex,
		);
		setRecommendations(currentDayIssues);
	}, [
		hospitalData,
		dayIndex,
		userInfo,
		weekIndex,
		isSnf,
		chosenDepartment,
		departmentNurseTypes,
		staffRequests,
		calculateShiftTimes,
		jobManagementEnabled,
		lastNameFirst,
	]);

	const handleRemoveRecommendation = useCallback((index: number): void => setRecommendations((prev) => prev.filter((_, i) => i !== index)), []);

	useEffect(() => getRecommendations(), [getRecommendations]);

	return {
		recommendations,
		getRecommendations,
		handleRemoveRecommendation,
		chosenLevel,
		setChosenLevel,
		implementedChanges,
		setImplementedChanges,
		startingDate,
	};
};
